import { GlIcon, GlTooltipDirective } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { debounce, isEqual } from 'lodash-es';
import { createAlert } from '../../../alert.js?vue3';
import GLForm from '../../../gl_form.js?vue3';
import SafeHtml from '~/vue_shared/directives/safe_html';
import axios from '../../../lib/utils/axios_utils.js?vue3';
import { __, sprintf } from '~/locale';
import Suggestions from './suggestions.vue?vue3';
import { renderGFM } from '../../../behaviors/markdown/render_gfm.js?vue3';
import { MARKDOWN_EDITOR_READY_EVENT } from '~/vue_shared/constants';
import markdownEditorEventHub from '~/vue_shared/components/markdown/eventhub';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
import MarkdownHeader from './header.vue?vue3';
import MarkdownToolbar from './toolbar.vue?vue3';
export default {
    name: 'MarkdownField',
    components: {
        MarkdownHeader,
        MarkdownToolbar,
        GlIcon,
        Suggestions
    },
    directives: {
        SafeHtml,
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        glSlotsMixin
    ],
    props: {
        /**
     * This prop should be bound to the value of the `<textarea>` element
     * that is rendered as a child of this component (in the `textarea` slot)
     */ textareaValue: {
            type: String,
            required: true
        },
        markdownDocsPath: {
            type: String,
            required: true
        },
        isSubmitting: {
            type: Boolean,
            required: false,
            default: false
        },
        markdownPreviewPath: {
            type: String,
            required: false,
            default: ''
        },
        newCommentTemplatePaths: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        enablePreview: {
            type: Boolean,
            required: false,
            default: true
        },
        supportsQuickActions: {
            type: Boolean,
            required: false,
            default: false
        },
        supportsTableOfContents: {
            type: Boolean,
            required: false,
            default: false
        },
        canAttachFile: {
            type: Boolean,
            required: false,
            default: true
        },
        uploadsPath: {
            type: String,
            required: false,
            default: ''
        },
        enableAutocomplete: {
            type: Boolean,
            required: false,
            default: true
        },
        autocompleteDataSources: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        codeSuggestionsConfig: {
            type: Object,
            required: false,
            default: ()=>({
                    lines: [],
                    lineType: '',
                    canSuggest: false,
                    showPopover: false
                })
        },
        note: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        helpPagePath: {
            type: String,
            required: false,
            default: ''
        },
        showCommentToolBar: {
            type: Boolean,
            required: false,
            default: true
        },
        restrictedToolBarItems: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        showContentEditorSwitcher: {
            type: Boolean,
            required: false,
            default: false
        },
        drawioEnabled: {
            type: Boolean,
            required: false,
            default: false
        },
        editorAiActions: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        immersive: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'enable-content-editor',
        'handle-suggest-dismissed'
    ],
    data () {
        return {
            glForm: null,
            markdownPreview: '',
            referencedCommands: '',
            referencedUsers: [],
            hasSuggestion: false,
            markdownPreviewLoading: false,
            previewMarkdown: false,
            suggestions: this.note.suggestions || [],
            debouncedFetchMarkdownLoading: false
        };
    },
    computed: {
        shouldShowReferencedUsers () {
            const referencedUsersThreshold = 10;
            return this.referencedUsers.length >= referencedUsersThreshold;
        },
        lineContent () {
            return this.codeSuggestionsConfig.lines.join('\\n');
        },
        addMultipleToDiscussionWarning () {
            return sprintf(__('You are about to add %{usersTag} people to the discussion. They will all receive a notification.'), {
                usersTag: `<strong><span class="js-referenced-users-count">${this.referencedUsers.length}</span></strong>`
            }, false);
        },
        suggestionsStartIndex () {
            return Math.max(this.codeSuggestionsConfig.lines.length - 1, 0);
        }
    },
    watch: {
        isSubmitting (isSubmitting) {
            if (!isSubmitting || !this.$refs['markdown-preview'].querySelectorAll) {
                return;
            }
            const mediaInPreview = this.$refs['markdown-preview'].querySelectorAll('video, audio');
            if (mediaInPreview) {
                mediaInPreview.forEach((media)=>{
                    media.pause();
                });
            }
        },
        textareaValue: {
            immediate: true,
            handler (textareaValue, oldVal) {
                this.hidePreview();
                const all = /@all([^\w._-]|$)/;
                const hasAll = all.test(textareaValue);
                const hadAll = all.test(oldVal);
                const justAddedAll = !hadAll && hasAll;
                const justRemovedAll = hadAll && !hasAll;
                if (justAddedAll) {
                    this.debouncedFetchMarkdownLoading = false;
                    this.debouncedFetchMarkdown();
                } else if (justRemovedAll) {
                    this.debouncedFetchMarkdownLoading = true;
                    this.referencedUsers = [];
                }
            }
        },
        enablePreview: {
            immediate: true,
            handler (newVal) {
                if (!newVal) {
                    this.hidePreview();
                }
            }
        },
        autocompleteDataSources: {
            immediate: true,
            handler (newDataSources, oldDataSources) {
                if (!isEqual(newDataSources, oldDataSources) && this.glForm) {
                    this.glForm.updateAutocompleteDataSources(newDataSources);
                }
            }
        }
    },
    mounted () {
        this.glForm = new GLForm(this.$refs['gl-form'], {
            emojis: this.enableAutocomplete,
            members: this.enableAutocomplete,
            issues: this.enableAutocomplete,
            issuesAlternative: this.enableAutocomplete,
            workItems: this.enableAutocomplete,
            mergeRequests: this.enableAutocomplete,
            epics: this.enableAutocomplete,
            epicsAlternative: this.enableAutocomplete,
            milestones: this.enableAutocomplete,
            labels: this.enableAutocomplete,
            snippets: this.enableAutocomplete,
            vulnerabilities: this.enableAutocomplete,
            contacts: this.enableAutocomplete,
            statuses: this.enableAutocomplete
        }, true, this.autocompleteDataSources);
        markdownEditorEventHub.$emit(MARKDOWN_EDITOR_READY_EVENT);
    },
    beforeDestroy () {
        if (this.glForm) {
            this.glForm.destroy();
        }
    },
    methods: {
        showPreview () {
            if (this.previewMarkdown) return;
            this.previewMarkdown = true;
            if (this.textareaValue) {
                this.markdownPreviewLoading = true;
                this.markdownPreview = __('Loading…');
                this.fetchMarkdown().then((data)=>this.renderMarkdown(data)).catch(()=>createAlert({
                        message: __('Error loading markdown preview')
                    }));
            } else {
                this.renderMarkdown();
            }
        },
        hidePreview () {
            this.markdownPreview = '';
            this.previewMarkdown = false;
        },
        fetchMarkdown () {
            const params = {
                text: this.textareaValue,
                no_header_anchors: !this.supportsTableOfContents
            };
            return axios.post(this.markdownPreviewPath, params).then(({ data })=>{
                const { references } = data;
                if (references) {
                    this.referencedCommands = references.commands;
                    this.referencedUsers = references.users;
                    this.hasSuggestion = references.suggestions?.length > 0;
                    this.suggestions = references.suggestions;
                }
                return data;
            });
        },
        debouncedFetchMarkdown: debounce(function debouncedFetchMarkdown() {
            return this.fetchMarkdown().then(()=>{
                if (this.debouncedFetchMarkdownLoading) {
                    this.referencedUsers = [];
                    this.debouncedFetchMarkdownLoading = false;
                }
            });
        }, 400),
        renderMarkdown (data = {}) {
            const { references } = data;
            if (!references) {
                this.referencedCommands = '';
            }
            this.markdownPreviewLoading = false;
            this.markdownPreview = data.body || data.html || __('Nothing to preview.');
            this.$nextTick().then(()=>{
                renderGFM(this.$refs['markdown-preview']);
            }).catch(()=>createAlert({
                    message: __('Error rendering Markdown preview')
                }));
        }
    },
    safeHtmlConfig: {
        ADD_TAGS: [
            'gl-emoji'
        ]
    }
};
